import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Globe, Bot } from "lucide-react";
import { API } from "../../lib/site";

const ICONS = {
  pass: <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600 shrink-0" />,
  warn: <AlertTriangle className="w-4.5 h-4.5 text-amber-500 shrink-0" />,
  fail: <XCircle className="w-4.5 h-4.5 text-red-500 shrink-0" />,
};

export default function SeoTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/admin/seo-report`, { withCredentials: true })
      .then((res) => setReport(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  if (loading || !report) {
    return <p className="text-sm text-slate-400 py-10 text-center font-mono animate-pulse">Analysing your website…</p>;
  }

  return (
    <div data-testid="admin-seo-tab" className="max-w-3xl space-y-6">
      <div className="bg-white border border-sand-border rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-7">
        <div className="relative w-32 h-32 shrink-0">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r="52" fill="none" stroke="#F3ECE1" strokeWidth="10" />
            <circle
              cx="60" cy="60" r="52" fill="none" stroke="#865F34" strokeWidth="10" strokeLinecap="round"
              strokeDasharray={`${(report.score / 100) * 326.7} 326.7`}
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span data-testid="seo-score" className="font-serif text-4xl text-reef leading-none">{report.score}</span>
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-slate-400 mt-1">/ 100</span>
          </div>
        </div>
        <div>
          <h3 className="font-serif text-2xl text-slate-900">SEO &amp; AI-search health</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            {report.passed} of {report.total} checks passing. This measures how easily Google
            <em> and </em> AI assistants (ChatGPT, Gemini, Perplexity) can find, understand and
            recommend Reef Deco to people searching for curtains in Dubai.
          </p>
          <button
            data-testid="seo-refresh-btn"
            onClick={load}
            className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-full border border-sand-border text-slate-600 font-mono text-[11px] tracking-[0.12em] uppercase hover:border-reef/40 hover:text-reef transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Re-run analysis
          </button>
        </div>
      </div>

      {report.groups.map((g, gi) => (
        <div key={g.name} className="bg-white border border-sand-border rounded-2xl p-6 sm:p-8">
          <p className="flex items-center gap-2.5 font-mono text-[11px] tracking-[0.2em] uppercase text-reef mb-5">
            {gi === 0 ? <Globe className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
            {g.name}
          </p>
          <ul className="space-y-3.5">
            {g.checks.map((c, ci) => (
              <li key={ci} data-testid={`seo-check-${gi}-${ci}`} className="flex items-start gap-3">
                {ICONS[c.status]}
                <div>
                  <p className="text-sm font-medium text-slate-800">{c.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{c.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="bg-reef-light/60 border border-reef/15 rounded-2xl p-6">
        <p className="text-sm text-slate-700 leading-relaxed">
          <strong>Tip:</strong> your page title, description and keywords are editable under
          <strong> Site Content → SEO &amp; AEO</strong>. Publishing one blog article a month is the
          single biggest booster for AI-search visibility.
        </p>
      </div>
    </div>
  );
}
